import type { Thread } from '@wovin/core'
import type { Applog, ApplogForInsertOptionalAgent } from '@wovin/core/applog'
import type { ArrayOrSingle } from '@wovin/core/types'
import type { ThreadIDB } from './datalog/local-applog-idb'

import { withoutDeleted } from '@wovin/core'
import { hasAg } from '@wovin/core/applog'
import { arrayIfSingle } from '@wovin/core/types'
import { Logger } from 'besonders-logger'
import { loadAppThread, sanityCheck } from './loadAppThread'

const { WARN, LOG, DEBUG, VERBOSE, ERROR } = Logger.setup(Logger.INFO) // eslint-disable-line unused-imports/no-unused-vars

// let applogDB: DatalogDB
export type AppThread = ThreadIDB // prep for tagged type
let applogDB: ThreadIDB | null | true

// Registered by AgentState on module init — breaks the AgentState ↔ ApplogDB cycle.
// AgentState.tsx MUST be imported (directly or transitively) before any insert path runs,
// otherwise inserts will throw `missing agentHash`. Today appInit.ts is imported first
// from App.tsx, satisfying this.
// Stashed on import.meta.hot.data so the registration survives ApplogDB HMR re-eval
// (AgentState.tsx isn't guaranteed to re-run when only this module is invalidated).
let getAgRef: () => string | undefined = import.meta.hot?.data.getAgRef ?? (() => undefined)
export function setAgentHashGetter(fn: () => string | undefined) {
	getAgRef = fn
	if (import.meta.hot) import.meta.hot.data.getAgRef = fn
}

export async function initApplogDB() {
	if (applogDB) {
		if (applogDB === true) { // = initializing
			if (import.meta.env.DEV) window.location.reload() // HACK can't be bothered to fix HMR
			throw ERROR('concurrent attempts to initApplogDB')
		}
		WARN('[initApplogDB] already initialized') // happens on HMR
		return applogDB
	}
	applogDB = true // to trigger the above while we're initializing

	// saturate from IDB
	// @ts-expect-error not sure how to do this right
	applogDB = await loadAppThread() as Omit<ThreadIDB, 'filters'> & { filters: [] }

	if (typeof window != 'undefined') {
		// @ts-expect-error because window
		window.db = applogDB
		// @ts-expect-error because window
		window.appThread = applogDB
		// @ts-expect-error because window
		window.thread = applogDB // it's nice to be able to copy-paste into devtools
		// @ts-expect-error because window
		window.currentThread = withoutDeleted(lastWriteWins(applogDB))
		// @ts-expect-error because window
		window.insertApplogs = insertApplogs
	}
	return applogDB as ThreadIDB
}

export function getApplogDB() {
	if (!applogDB) {
		if (import.meta.hot) {
			window.location.reload() // HACK to work around HMR issue
		}
		throw new Error(`Uninitizalized`)
	}
	if (applogDB === true) {
		throw new Error(`ApplogDB init not done yet`)
	}
	return applogDB
}

export const addAgentToApplog = function addAgentToApplog(log: ApplogForInsertOptionalAgent, ag?: string) {
	ag = ag ?? getAgRef()
	if (!ag) throw ERROR(`missing agentHash`)
	return hasAg(log) ? log : ({ ...log, ag }) /* , vl: log.vl //? was this a brain fart or still relevant */
}
export const addAgentToApplogs = function addAgentToApplogs(applogs: readonly ApplogForInsertOptionalAgent[]) {
	const ag = getAgRef()
	if (!ag) throw ERROR(`missing agentHash`)
	return applogs.filter(log => !!log).map(log => addAgentToApplog(log, ag)) // filter is to purge falsy logs
}

export function insertApplogs(thread: Thread, applogs: ArrayOrSingle<ApplogForInsertOptionalAgent>) {
	return thread.insert(addAgentToApplogs(arrayIfSingle(applogs)))
}
export function insertApplogsInAppDB(applogs: ArrayOrSingle<ApplogForInsertOptionalAgent>) {
	return getApplogDB().insert(addAgentToApplogs(arrayIfSingle(applogs)))
}
export function insertCompleteApplogsInAppDBifMissing(applogs: ArrayOrSingle<Applog>) {
	const missingApplogs = arrayIfSingle(applogs).filter(log => !(getApplogDB().hasApplog(log, false)))
	return getApplogDB().insert(sanityCheck(missingApplogs))
}

if (import.meta.hot) {
	import.meta.hot.accept((module) => {
		// You may use the new module instance to decide whether to invalidate.
		// if (cannotHandleUpdate(module)) {
		LOG(`HMR applogDB - invalidated `, import.meta.hot, module)
		import.meta.hot.invalidate()
		// }
	})
	// void initApplogDB()
}
